import React from 'react';
import { Link } from 'react-router-dom';
import { IconLinkedIn } from './Icons';
import { useLanguage } from '../context/LanguageContext';
import './Footer.css';

const IconInstagram = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const IconYouTube = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const Footer = () => {
  const { t, toPath } = useLanguage();

  const quickLinks = [
    { name: t.footer.quickLink_ppc, href: '#ppc' },
    { name: t.footer.quickLink_images, href: '#images' },
    { name: t.footer.quickLink_portfolio, href: '/portfolio' },
    { name: t.footer.quickLink_references, href: '#testimonials' },
    { name: t.footer.quickLink_about, href: '#team' },
    { name: t.footer.quickLink_faq, href: '#faq' },
  ];

  const supportLinks = [
    { name: t.footer.support_contact, href: 'mailto:info@foxperformance.de' },
    { name: t.footer.support_booking, href: 'https://meetings-eu1.hubspot.com/michael-wenger?uuid=9cf1d252-81d9-4d0a-ac62-21972be61c70' },
    { name: t.footer.support_linkedin, href: 'https://linkedin.com' },
    { name: t.footer.support_instagram, href: 'https://instagram.com' },
  ];

  const legalLinks = [
    { name: t.footer.legal_imprint, href: '/impressum' },
    { name: t.footer.legal_privacy, href: '/datenschutz' },
    { name: t.footer.legal_terms, href: '/agb' },
    { name: t.footer.legal_cookies, href: '#cookies' },
  ];

  return (
    <footer className="footer-fox">
      <div className="footer-fox__glow footer-fox__glow--1"></div>
      <div className="footer-fox__glow footer-fox__glow--2"></div>
      
      <div className="footer-fox__container">
        <div className="footer-fox__content">
          <div className="footer-fox__brand">
            <div className="footer-fox__logo">
              <img src="/img/fox_icon.png" alt="Fox Performance" className="footer-fox__logo-img" width={40} height={40} loading="lazy" />
              <span className="footer-fox__logo-text">{t.footer.logoText}</span>
            </div>
            <div className="footer-fox__social">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="footer-fox__social-link" aria-label="LinkedIn"><IconLinkedIn size={18} /></a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="footer-fox__social-link" aria-label="Instagram"><IconInstagram size={18} /></a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="footer-fox__social-link" aria-label="YouTube"><IconYouTube size={18} /></a>
            </div>
            <p className="footer-fox__copyright">
              © {new Date().getFullYear()} Fox Performance<br />{t.footer.copyright}
            </p>
          </div>

          <div className="footer-fox__column">
            <h4 className="footer-fox__column-title">{t.footer.quickLinksTitle}</h4>
            <ul className="footer-fox__links">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  {link.href.startsWith('/') ? (
                    <Link to={toPath(link.href)} className="footer-fox__link">{link.name}</Link>
                  ) : (
                    <a href={link.href} className="footer-fox__link">{link.name}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-fox__column">
            <h4 className="footer-fox__column-title">{t.footer.supportTitle}</h4>
            <ul className="footer-fox__links">
              {supportLinks.map((link, index) => (
                <li key={index}>
                  <a href={link.href} target={link.href.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer" className="footer-fox__link">{link.name}</a>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-fox__column">
            <h4 className="footer-fox__column-title">{t.footer.legalTitle}</h4>
            <ul className="footer-fox__links">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  {link.href.startsWith('/') ? (
                    <Link to={toPath(link.href)} className="footer-fox__link">{link.name}</Link>
                  ) : (
                    <a href={link.href} className="footer-fox__link">{link.name}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
